import { SystemModule, Role, DepartmentRole } from '@21k/shared';

import type { ModulePermission, TicketDetail, UserProfile } from '../api/types';

export function isSuperAdmin(role: Role): boolean {
  return role === Role.SUPER_ADMIN;
}

function permFor(user: UserProfile, module: SystemModule): ModulePermission | undefined {
  if (isSuperAdmin(user.role)) {
    return {
      module,
      canRead: true,
      canWrite: true,
      canEdit: true,
      canExport: true,
    };
  }
  return user.effectivePermissions?.find((p) => p.module === module);
}

export function canReadModule(user: UserProfile, module: SystemModule): boolean {
  return Boolean(permFor(user, module)?.canRead);
}

export function canWriteModule(user: UserProfile, module: SystemModule): boolean {
  return Boolean(permFor(user, module)?.canWrite);
}

export function canEditModule(user: UserProfile, module: SystemModule): boolean {
  return Boolean(permFor(user, module)?.canEdit);
}

export function canExportModule(user: UserProfile, module: SystemModule): boolean {
  return Boolean(permFor(user, module)?.canExport);
}

export function canAccessRoleManagement(user: UserProfile): boolean {
  return canReadModule(user, SystemModule.ROLE_MANAGEMENT);
}

export function canWriteRoles(user: UserProfile): boolean {
  return canWriteModule(user, SystemModule.ROLE_MANAGEMENT);
}

export function canEditRoles(user: UserProfile): boolean {
  return canEditModule(user, SystemModule.ROLE_MANAGEMENT);
}

export function canAccessStaffManagement(user: UserProfile): boolean {
  return canReadModule(user, SystemModule.STAFF_MANAGEMENT);
}

export function canWriteStaff(user: UserProfile): boolean {
  return canWriteModule(user, SystemModule.STAFF_MANAGEMENT);
}

export function canEditStaff(user: UserProfile): boolean {
  return canEditModule(user, SystemModule.STAFF_MANAGEMENT);
}

export function canExportStaff(user: UserProfile): boolean {
  return canExportModule(user, SystemModule.STAFF_MANAGEMENT);
}

export function canAccessDepartments(user: UserProfile): boolean {
  return canReadModule(user, SystemModule.DEPARTMENT_MANAGEMENT);
}

export function canWriteDepartments(user: UserProfile): boolean {
  return canWriteModule(user, SystemModule.DEPARTMENT_MANAGEMENT);
}

export function canEditDepartments(user: UserProfile): boolean {
  return canEditModule(user, SystemModule.DEPARTMENT_MANAGEMENT);
}

export function canExportDepartments(user: UserProfile): boolean {
  return canExportModule(user, SystemModule.DEPARTMENT_MANAGEMENT);
}

export function canAccessPlatformConfig(user: UserProfile): boolean {
  return canReadModule(user, SystemModule.PLATFORM_CONFIG);
}

/** Show Platform Config nav when the user can access any section inside it. */
export function canAccessPlatformConfigSection(user: UserProfile): boolean {
  return (
    canAccessPlatformConfig(user) ||
    canAccessDepartments(user) ||
    canViewSla(user)
  );
}

export function getDefaultPlatformConfigPath(user: UserProfile): string {
  if (canAccessPlatformConfig(user)) return '/platform-config/channel-types';
  if (canAccessDepartments(user)) return '/platform-config/departments';
  if (canViewSla(user)) return '/platform-config/sla';
  return '/tickets';
}

export function canWritePlatformConfig(user: UserProfile): boolean {
  return canWriteModule(user, SystemModule.PLATFORM_CONFIG);
}

export function canEditPlatformConfig(user: UserProfile): boolean {
  return canEditModule(user, SystemModule.PLATFORM_CONFIG);
}

export function canViewSla(user: UserProfile): boolean {
  return canReadModule(user, SystemModule.SLA_MANAGEMENT);
}

export function canWriteSla(user: UserProfile): boolean {
  return canWriteModule(user, SystemModule.SLA_MANAGEMENT);
}

export function canEditSla(user: UserProfile): boolean {
  return canEditModule(user, SystemModule.SLA_MANAGEMENT);
}

export function canExportSla(user: UserProfile): boolean {
  return canExportModule(user, SystemModule.SLA_MANAGEMENT);
}

export function canViewReports(user: UserProfile): boolean {
  return canReadModule(user, SystemModule.REPORTS);
}

export function canExportReports(user: UserProfile): boolean {
  return canExportModule(user, SystemModule.REPORTS);
}

export function canExportTickets(user: UserProfile): boolean {
  return canExportModule(user, SystemModule.TICKET_MANAGEMENT);
}

/** Owner, dept admin, super admin, or TICKET_MANAGEMENT canEdit. Creating a ticket does not grant this. */
export function canEditTicket(user: UserProfile, ticket: TicketDetail): boolean {
  if (isSuperAdmin(user.role)) return true;

  const ownerId = ticket.currentOwner?.assignedToId ?? ticket.assignedToId;
  if (ownerId === user.id) return true;

  const isDeptAdmin = user.departmentMemberships.some(
    (m) => m.departmentId === ticket.departmentId && m.role === DepartmentRole.DEPARTMENT_ADMIN,
  );
  if (isDeptAdmin) return true;

  if (ticket.createdById === user.id) return false;

  return canEditModule(user, SystemModule.TICKET_MANAGEMENT);
}

/** SUPER_ADMIN or TICKET_MANAGEMENT edit — may reply to parent on any ticket. */
export function canReplyToParentOnAnyTicket(user: UserProfile): boolean {
  if (isSuperAdmin(user.role)) return true;
  return canEditModule(user, SystemModule.TICKET_MANAGEMENT);
}

/** @deprecated Use isSuperAdmin */
export function isPrivileged(role: Role): boolean {
  return isSuperAdmin(role);
}

/** Only SUPER_ADMIN can promote others to SUPER_ADMIN */
export function canAssignSuperAdmin(user: UserProfile): boolean {
  return isSuperAdmin(user.role);
}

export function canAccessAdminSection(user: UserProfile): boolean {
  return (
    canAccessRoleManagement(user) ||
    canAccessStaffManagement(user) ||
    canAccessPlatformConfigSection(user)
  );
}

const TICKET_LIST_ALL_VIEW_ROLE_NAMES = new Set([
  'cx manager',
  'platform admin',
]);

/** Super Admin, Platform Admin, and CX Manager see all tickets in the list by default. */
export function canViewAllTicketsInList(user: UserProfile): boolean {
  if (isSuperAdmin(user.role)) return true;
  return (user.systemRoles ?? []).some(
    (assignment) =>
      assignment.role?.name &&
      TICKET_LIST_ALL_VIEW_ROLE_NAMES.has(assignment.role.name.trim().toLowerCase()),
  );
}
